import { useState, useMemo, useEffect } from 'react';

/**
 * Images: public/images/ → reference as /images/... (use imagePath() from config/siteImages.js).
 * Optional fallbackSrcs: try next URL on error so sections rarely show empty boxes.
 */
export default function SafeImage({
  src,
  alt = '',
  fallbackSrcs = [],
  wrapperClassName = '',
  imgClassName = '',
  loading = 'lazy',
}) {
  const chain = useMemo(() => {
    const raw = [src, ...fallbackSrcs].filter((u) => u && String(u).trim() !== '');
    return [...new Set(raw)];
  }, [src, fallbackSrcs]);

  const [index, setIndex] = useState(0);
  const [exhausted, setExhausted] = useState(chain.length === 0);

  const chainKey = chain.join('|');
  useEffect(() => {
    setIndex(0);
    setExhausted(chain.length === 0);
  }, [chainKey, chain.length]);

  const current = chain[index] || '';

  const handleError = () => {
    if (index < chain.length - 1) {
      setIndex((i) => i + 1);
    } else {
      setExhausted(true);
    }
  };

  if (exhausted || !current) {
    return (
      <div className={wrapperClassName}>
        <div
          className="safe-img-fallback safe-img-fallback--minimal"
          role="img"
          aria-label={alt || 'Image unavailable'}
        />
      </div>
    );
  }

  return (
    <div className={wrapperClassName}>
      <img
        key={current}
        src={current}
        alt={alt}
        className={imgClassName}
        loading={loading}
        decoding="async"
        onError={handleError}
      />
    </div>
  );
}
